import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout.tsx';
import { OrganizationForm } from '@/components/forms/OrganizationForm';
import { ContactsList } from '@/components/contacts/ContactsList';
import { StagedContactModal } from '@/components/contacts/StagedContactModal';
import { useCreateOrganization, useCreateOrganizationContact } from '@/hooks';
import type { OrganizationFormData, OrganizationContact } from '@/lib/schemas';

// Type for staged contacts (before org is created, so no id/timestamps)
interface StagedContact {
  tempId: string; // Temporary ID for React keys
  name: string;
  email?: string;
  phone?: string;
  title?: string;
  is_primary_contact: boolean;
}

export function OrganizationCreatePage() {
  const navigate = useNavigate();
  const createOrganization = useCreateOrganization();
  const createContact = useCreateOrganizationContact();

  const [stagedContacts, setStagedContacts] = useState<StagedContact[]>([]);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddStagedContact = (contact: Omit<StagedContact, 'tempId'>) => {
    const newContact: StagedContact = {
      ...contact,
      tempId: `temp-${Date.now()}-${Math.random()}`,
    };
    setStagedContacts([...stagedContacts, newContact]);
  };

  const handleDeleteStagedContact = (tempId: string) => {
    setStagedContacts(stagedContacts.filter(c => c.tempId !== tempId));
  };

  const handleSetPrimaryStagedContact = (tempId: string) => {
    setStagedContacts(stagedContacts.map(c => ({
      ...c,
      is_primary_contact: c.tempId === tempId,
    })));
  };

  const handleSubmit = async (data: OrganizationFormData) => {
    const payload = {
      ...data,
      legal_name: data.legal_name || null,
      email: data.email || null,
      phone: data.phone || null,
      address: data.address || null,
      billing_address: data.billing_address || null,
      notes: data.notes || null,
      payment_terms: data.payment_terms ? parseInt(data.payment_terms) : 30,
      // Phase 5 Batch 6 Phase 4: Use role flags from form data
      is_client: data.is_client,
      is_consultant: data.is_consultant,
      is_government: data.is_government,
      is_self: false, // System-managed, not editable by users
      // Phase 6 Batch 2: Organization category (optional)
      category_id: data.category_id || null,
    };

    try {
      setIsSubmitting(true);

      // Step 1: Create organization
      const response = await createOrganization.mutateAsync(payload);
      const newOrgId = response.organization.id;

      // Step 2: Create all staged contacts
      if (stagedContacts.length > 0) {
        await Promise.all(
          stagedContacts.map(contact =>
            createContact.mutateAsync({
              organizationId: newOrgId,
              name: contact.name,
              email: contact.email || undefined,
              phone: contact.phone || undefined,
              title: contact.title || undefined,
              is_primary_contact: contact.is_primary_contact,
            })
          )
        );
      }

      navigate('/organizations');
    } catch (error: any) {
      console.error('Failed to create organization:', error);
      // Handle duplicate name error specifically
      if (error?.message?.includes('duplicate') || error?.message?.includes('unique_organization_name_per_tenant')) {
        createOrganization.error = {
          message: `A client with the name "${data.name}" already exists. Please use a different name.`
        } as any;
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Convert staged contacts to OrganizationContact format for ContactsList
  const contactsForDisplay: OrganizationContact[] = stagedContacts.map(c => ({
    id: c.tempId, // Use tempId as id for display purposes
    organization_id: '', // Not relevant for staging
    tenant_id: '', // Not relevant for staging
    name: c.name,
    email: c.email || null,
    phone: c.phone || null,
    title: c.title || null,
    notes: null,
    is_primary_contact: c.is_primary_contact,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  }));

  return (
    <>
      <DashboardLayout title="New Client">
        {/* Two-column grid: form | staged contacts */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Left column: Organization details */}
          <div className="space-y-6">
            <OrganizationForm
              mode="create"
              onSubmit={handleSubmit}
              onCancel={() => navigate('/organizations')}
            />

            {createOrganization.isError && (
              <p className="text-sm text-destructive">
                {createOrganization.error?.message || 'Failed to create client'}
              </p>
            )}
          </div>

          {/* Right column: Staged Contacts */}
          <div className="space-y-6">
            <ContactsList
              contacts={contactsForDisplay}
              onAddContact={() => setShowAddContactModal(true)}
              onDeleteContact={handleDeleteStagedContact}
              onSetPrimary={handleSetPrimaryStagedContact}
            />
          </div>
        </div>
      </DashboardLayout>

      {/* Staged Contact Modal */}
      <StagedContactModal
        open={showAddContactModal}
        onOpenChange={setShowAddContactModal}
        onContactAdded={handleAddStagedContact}
      />
    </>
  );
}
